"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { track } from "../core/client";
import { ulid } from "ulid";

/**
 * 모달 추적 Context
 */
interface ModalTrackingContext {
  modalId: string;
  instanceId: string | null;
  openTime: number | null;
  trackClose: (reason: "backdrop" | "cancel" | "success") => void;
}

const ModalContext = createContext<ModalTrackingContext | null>(null);

/**
 * 모달 추적 Context Provider
 *
 * 모달의 open/close를 자동으로 추적하고,
 * 모달 내부에서 useModalClose()로 reason을 전달할 수 있습니다.
 *
 * @example
 * ```tsx
 * // 1. Provider로 감싸기 (외부)
 * <TrackedModalProvider
 *   modalId="news_rader_detail_modal"
 *   isOpen={open}
 *   triggerSectionId="main_news_section"
 * >
 *   {open && <NewLaderDetailModal />}
 * </TrackedModalProvider>
 *
 * // 2. 모달 내부에서 사용
 * function NewLaderDetailModal() {
 *   const { trackClose } = useModalClose();
 *
 *   return (
 *     <BottomModal>
 *       <Icon onClick={() => trackClose('cancel')}>닫기</Icon>
 *       <div onClick={() => trackClose('backdrop')}>바깥 클릭</div>
 *       <Button onClick={() => trackClose('success')}>확인</Button>
 *     </BottomModal>
 *   );
 * }
 * ```
 */
export interface TrackedModalProviderProps {
  modalId: string;
  isOpen: boolean;
  children: React.ReactNode;
  triggerSectionId?: string;
  intentId?: string;
  triggerType?: "user_click" | "auto";
}

export function TrackedModalProvider({
  modalId,
  isOpen,
  children,
  triggerSectionId,
  intentId,
  triggerType = "user_click",
}: TrackedModalProviderProps) {
  const instanceIdRef = useRef<string | null>(null);
  const openTimeRef = useRef<number | null>(null);
  const prevOpenRef = useRef(false);

  // trackClose 함수
  const trackClose = useCallback(
    (reason: "backdrop" | "cancel" | "success") => {
      if (instanceIdRef.current && openTimeRef.current) {
        const stayMs = Date.now() - openTimeRef.current;

        track("modal_close", {
          modal_id: modalId,
          modal_instance_id: instanceIdRef.current,
          stay_ms: stayMs,
          reason,
        });

        instanceIdRef.current = null;
        openTimeRef.current = null;
      }
    },
    [modalId]
  );

  // isOpen 변화 감지
  useEffect(() => {
    // 모달이 열렸을 때
    if (isOpen && !prevOpenRef.current) {
      const instanceId = ulid();
      instanceIdRef.current = instanceId;
      openTimeRef.current = Date.now();

      track("modal_open", {
        modal_id: modalId,
        modal_instance_id: instanceId,
        trigger_section_id: triggerSectionId,
        intent_id: intentId,
        trigger_type: triggerType,
      });
    }

    prevOpenRef.current = isOpen;
  }, [isOpen, modalId, triggerSectionId, intentId, triggerType]);

  // Context value
  const contextValue: ModalTrackingContext = {
    modalId,
    instanceId: instanceIdRef.current,
    openTime: openTimeRef.current,
    trackClose,
  };

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
    </ModalContext.Provider>
  );
}

/**
 * 모달 내부에서 사용하는 훅
 *
 * TrackedModalProvider 내부에서만 사용 가능합니다.
 *
 * @example
 * ```tsx
 * function MyModal() {
 *   const { trackClose } = useModalClose();
 *
 *   return (
 *     <>
 *       <Icon onClick={() => trackClose('cancel')}>닫기</Icon>
 *       <Button onClick={() => trackClose('success')}>확인</Button>
 *     </>
 *   );
 * }
 * ```
 */
/**
 * 모달 닫기 훅
 *
 * TrackedModalProvider 내부에서 사용하면 tracking 활성화
 * Provider 외부에서도 안전하게 사용 가능 (tracking 비활성화)
 *
 * @example
 * ```tsx
 * // Provider 내부 - tracking 활성화
 * <TrackedModalProvider modalId="my_modal" isOpen={open}>
 *   <MyModal /> // useModalClose() 사용 가능
 * </TrackedModalProvider>
 *
 * // Provider 외부 - tracking 비활성화 (no-op)
 * <MyModal /> // useModalClose() 사용 가능하지만 tracking 안됨
 * ```
 */
export function useModalClose() {
  const context = useContext(ModalContext);

  // Provider 외부에서 사용 시 no-op 반환 (에러 대신 조용히 무시)
  if (!context) {
    return {
      modalId: null,
      trackClose: (reason?: string) => {},
    };
  }

  return {
    modalId: context.modalId || "",
    trackClose: context.trackClose,
  };
}

/**
 * 모달/바텀시트 추적 래퍼 컴포넌트
 *
 * @deprecated 이 컴포넌트는 deprecated되었습니다. TrackedModalProvider를 사용하세요.
 * TrackedModalProvider는 Context를 통해 모달 내부에서 reason을 정확하게 추적할 수 있습니다.
 *
 * 기존 BottomModal을 그대로 사용하면서 자동으로 추적합니다.
 *
 * @example
 * ```tsx
 * // Before
 * {open && (
 *   <NewLaderDetailModal setModalOpen={setOpen} isModalOpen={open} />
 * )}
 *
 * // After (minimal change)
 * <TrackedBottomModal
 *   modalId="news_rader_detail_modal"
 *   isOpen={open}
 *   onClose={(reason) => setOpen(false)}
 * >
 *   <NewLaderDetailModal setModalOpen={setOpen} isModalOpen={open} />
 * </TrackedBottomModal>
 * ```
 */
export interface TrackedBottomModalProps {
  modalId: string;
  isOpen: boolean;
  onClose: (reason: "backdrop" | "cancel" | "success") => void;
  children: React.ReactNode;
  triggerSectionId?: string;
  intentId?: string;
  triggerType?: "user_click" | "auto";
}

export function TrackedBottomModal({
  modalId,
  isOpen,
  onClose,
  children,
  triggerSectionId,
  intentId,
  triggerType = "user_click",
}: TrackedBottomModalProps) {
  const instanceIdRef = useRef<string | null>(null);
  const openTimeRef = useRef<number | null>(null);
  const prevOpenRef = useRef(false);

  useEffect(() => {
    // 모달이 열렸을 때
    if (isOpen && !prevOpenRef.current) {
      const instanceId = ulid();
      instanceIdRef.current = instanceId;
      openTimeRef.current = Date.now();

      track("modal_open", {
        modal_id: modalId,
        modal_instance_id: instanceId,
        trigger_section_id: triggerSectionId,
        intent_id: intentId,
        trigger_type: triggerType,
      });
    }

    // 모달이 닫혔을 때
    if (!isOpen && prevOpenRef.current) {
      if (instanceIdRef.current && openTimeRef.current) {
        const stayMs = Date.now() - openTimeRef.current;

        // Note: reason은 알 수 없으므로 'cancel'로 기본값 설정
        // 정확한 reason이 필요하면 onClose에서 전달받아야 함
        track("modal_close", {
          modal_id: modalId,
          modal_instance_id: instanceIdRef.current,
          stay_ms: stayMs,
          reason: "cancel", // 기본값
        });

        instanceIdRef.current = null;
        openTimeRef.current = null;
      }
    }

    prevOpenRef.current = isOpen;
  }, [isOpen, modalId, triggerSectionId, intentId, triggerType]);

  // isOpen이 false면 children을 렌더링하지 않음
  if (!isOpen) return null;

  return <>{children}</>;
}

/**
 * setModalOpen을 추적하는 헬퍼 함수 생성
 *
 * 기존 코드를 거의 바꾸지 않고 사용 가능
 *
 * @example
 * ```tsx
 * // Before
 * const [open, setOpen] = useState(false);
 *
 * // After (only one line change)
 * const [open, setOpen] = useTrackedModalState('news_rader_detail_modal');
 *
 * // 기존 코드 그대로 동작
 * <div onClick={() => setOpen(true)}>더보기</div>
 * {open && <NewLaderDetailModal setModalOpen={setOpen} isModalOpen={open} />}
 * ```
 */
export function useTrackedModalState(
  modalId: string,
  options?: {
    triggerSectionId?: string;
    intentId?: string;
    triggerType?: "user_click" | "auto";
  }
) {
  const [isOpen, setIsOpenInternal] = React.useState(false);
  const instanceIdRef = useRef<string | null>(null);
  const openTimeRef = useRef<number | null>(null);

  const setIsOpen = React.useCallback(
    (newValue: boolean | ((prev: boolean) => boolean)) => {
      setIsOpenInternal((prev) => {
        const nextValue =
          typeof newValue === "function" ? newValue(prev) : newValue;

        // 열림: false -> true
        if (nextValue && !prev) {
          const instanceId = ulid();
          instanceIdRef.current = instanceId;
          openTimeRef.current = Date.now();

          track("modal_open", {
            modal_id: modalId,
            modal_instance_id: instanceId,
            trigger_section_id: options?.triggerSectionId,
            intent_id: options?.intentId,
            trigger_type: options?.triggerType || "user_click",
          });
        }

        // 닫힘: true -> false
        if (!nextValue && prev) {
          if (instanceIdRef.current && openTimeRef.current) {
            const stayMs = Date.now() - openTimeRef.current;

            track("modal_close", {
              modal_id: modalId,
              modal_instance_id: instanceIdRef.current,
              stay_ms: stayMs,
              reason: "cancel", // 기본값
            });

            instanceIdRef.current = null;
            openTimeRef.current = null;
          }
        }

        return nextValue;
      });
    },
    [modalId, options]
  );

  return [isOpen, setIsOpen] as const;
}

/**
 * 모달 닫기 함수에 reason을 추가하는 헬퍼
 *
 * @example
 * ```tsx
 * const [open, setOpen] = useState(false);
 * const trackedClose = useTrackedModalClose('news_modal', setOpen);
 *
 * // reason과 함께 닫기
 * <div onClick={() => trackedClose('backdrop')}>바깥 클릭</div>
 * <button onClick={() => trackedClose('cancel')}>취소</button>
 * <button onClick={() => trackedClose('success')}>확인</button>
 * ```
 */
export function useTrackedModalClose(
  modalId: string,
  setOpen: (open: boolean) => void
) {
  const instanceIdRef = useRef<string | null>(null);
  const openTimeRef = useRef<number | null>(null);

  // 모달 열림 감지를 위한 ref
  const trackOpen = React.useCallback((instanceId?: string) => {
    if (!instanceId) {
      instanceId = ulid();
    }
    instanceIdRef.current = instanceId;
    openTimeRef.current = Date.now();
    return instanceId;
  }, []);

  const trackClose = React.useCallback(
    (reason: "backdrop" | "cancel" | "success") => {
      if (instanceIdRef.current && openTimeRef.current) {
        const stayMs = Date.now() - openTimeRef.current;

        track("modal_close", {
          modal_id: modalId,
          modal_instance_id: instanceIdRef.current,
          stay_ms: stayMs,
          reason,
        });

        instanceIdRef.current = null;
        openTimeRef.current = null;
      }

      setOpen(false);
    },
    [modalId, setOpen]
  );

  return { trackOpen, trackClose };
}
